import type { MetricItemDTO } from '@/types/agentEvents';
import { formatPercentage, parsePercentToNumber } from './metricsAggregate';

// CSV utility functions
export function csvEscape(value: unknown): string {
  const s = value == null ? '' : String(value);
  if (/[",\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export function toCsv(rows: Array<Array<unknown>>): string {
  return rows.map(r => r.map(csvEscape).join(',')).join('\r\n');
}

export function downloadTextFile(
  filename: string,
  content: string,
  mimeType = 'text/csv;charset=utf-8'
) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export interface MetricsCsvColumn {
  header: string;
  value: (item: MetricItemDTO) => unknown;
}

export interface ExportMetricsCsvArgs {
  metric: string;
  rows: MetricItemDTO[];
  groupingKey: string;
  intervalPeriod?: string;
  startTime?: string;
  endTime?: string;
  columns?: MetricsCsvColumn[];
  filenameParts?: string[];
}

function sanitizeFilenamePart(value: string): string {
  // eslint-disable-next-line no-control-regex
  return value.replace(/[<>:"\\|?*]/g, '-').replace(/[\x00-\x1F]/g, '-');
}

export function exportMetricsCsv({
  metric,
  rows,
  groupingKey,
  intervalPeriod,
  startTime,
  endTime,
  columns = [],
  filenameParts = [],
}: ExportMetricsCsvArgs) {
  const header = [
    'metric',
    ...columns.map(c => c.header),
    'grouping',
    'groupingLabel',
    'intervalStartTime',
    'intervalEndTime',
    'value',
  ];

  const dataRows = rows.map(item => {
    let formattedValue = item.value;
    // Format percentage-based metrics
    if (
      [
        'occupancy',
        'abandonment-rate',
        'adherence',
        'service-level',
        'deflection-rate',
        'first-contact-resolution',
      ].includes(metric)
    ) {
      formattedValue = formatPercentage(parsePercentToNumber(item.value));
    }
    return [
      metric,
      ...columns.map(c => c.value(item)),
      groupingKey,
      (groupingKey && item.dimensionLabels?.[groupingKey]) ?? item.name,
      item.intervalStartTime ?? '',
      item.intervalEndTime ?? '',
      formattedValue,
    ];
  });

  const csv = toCsv([header, ...dataRows]);

  const timestamp =
    new Date().toISOString().replace(/[:.]/g, '-').split('T')[0] +
    '_' +
    new Date().toISOString().split('T')[1].replace(/[:.]/g, '-').slice(0, 6);

  const filename = [
    metric,
    ...filenameParts,
    intervalPeriod ?? '',
    startTime ?? '',
    endTime ?? '',
    timestamp,
  ]
    .filter(Boolean)
    .map(sanitizeFilenamePart)
    .join('_');

  downloadTextFile(`${filename}.csv`, csv);
}
